import React, { useMemo } from 'react';
import { Typography, Button, Box, Paper, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectGameId,
  selectGameStatusObject,
} from '../gamestate/gameStateSlice';
import {
  selectPlayer,
  selectAllPlayers,
  makeSelectPlayersReadinessForInterRound,
  updatePlayerStateAction,
} from '../player/playerSlice';
import { PLAYER_STATES } from '../player/playerConstants';
import { getInterRoundPhase } from '../player/playerConstants';
import { getRoundDisplayName, determineNextRound } from '../gamestate/roundCompletionUtils';
import { selectGameObject } from '../gamestate/gameStateSlice';
import Background from 'features/components/BackgroundEffect';
import PlayerLeaderboard from '../leaderboard/PlayerLeaderboard';

const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';
const CARD_BG = '#fff';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(46, 196, 182, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(46, 196, 182, 0); }
  100% { box-shadow: 0 0 0 0 rgba(46, 196, 182, 0); }
`;

const Root = styled('div')({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '16px',
  paddingBottom: '100px',
  position: 'relative',
});

const ContentCard = styled(Paper)(({ theme }) => ({
  backgroundColor: CARD_BG,
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  padding: theme.spacing(4, 3),
  maxWidth: 450,
  width: '100%',
  animation: `${fadeInUp} 0.6s ease-out`,
  textAlign: 'center',
  marginBottom: 80,
}));

const Title = styled(Typography)({
  fontFamily: "'Gyparody', 'Bangers', 'Impact', sans-serif",
  fontSize: '1.75rem',
  fontWeight: 800,
  color: TEXT_COLOR,
  marginBottom: 8,
  textShadow: '1px 1px 0px rgba(46, 196, 182, 0.3)',
});

const ReadyButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(120deg, ${ACCENT_YELLOW} 0%, ${ACCENT_TEAL} 50%, ${ACCENT_YELLOW} 100%)`,
  backgroundSize: '200% 200%',
  backgroundPosition: '0% 40%',
  color: TEXT_COLOR,
  padding: theme.spacing(1.5, 4),
  borderRadius: 12,
  fontWeight: 700,
  fontSize: '1.2em',
  textTransform: 'none',
  boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  minWidth: 240,
  position: 'fixed',
  bottom: theme.spacing(4),
  left: '50%',
  transform: 'translateX(-50%)',
  transition: 'background-position 0.5s cubic-bezier(0.4,0,0.2,1), box-shadow 0s',
  animation: `${pulse} 2s infinite`,
  '&:hover': {
    backgroundPosition: '100% 60%',
    boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  },
  '&.Mui-disabled': {
    background: '#e8e0c0',
    color: '#777570',
    boxShadow: 'none !important',
  }
}));

const PlayerInterRoundScreen = () => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const gameStatus = useSelector(selectGameStatusObject);
  const game = useSelector(selectGameObject);
  const player = useSelector(selectPlayer);
  const allPlayers = useSelector(selectAllPlayers);

  const round = gameStatus.round;
  const phase = getInterRoundPhase(round);

  const selectReadiness = useMemo(() => makeSelectPlayersReadinessForInterRound(round), [round]);
  const readiness = useSelector(selectReadiness);

  const isPlayerReady = player?.playerState?.[phase]?.state === PLAYER_STATES.INTER_ROUND.READY;
  const readyCount = Object.values(readiness).filter(p => p.isReady).length;
  const totalCount = allPlayers.length;

  const completedRound = getRoundDisplayName(round);
  const nextRound = determineNextRound(round, game?.categories);
  const nextRoundName = nextRound ? getRoundDisplayName(nextRound) : 'the next round';

  const handleReady = () => {
    if (gameId && player && !isPlayerReady) {
      dispatch(updatePlayerStateAction(
        gameId,
        player.id,
        phase,
        { state: PLAYER_STATES.INTER_ROUND.READY }
      ));
    }
  };

  if (!isPlayerReady) {
    return (
      <Root>
        <Background />
        <ContentCard>
          <Title>Round Complete!</Title>
          <Typography sx={{ color: TEXT_COLOR, mb: 2, opacity: 0.8 }}>
            {completedRound} has ended
          </Typography>
          <Typography sx={{ color: TEXT_COLOR, fontSize: '1rem' }}>
            Tap the button when you're ready for {nextRoundName}.
          </Typography>
        </ContentCard>
        <PlayerLeaderboard />
        <ReadyButton onClick={handleReady}>
          Ready for {nextRoundName}!
        </ReadyButton>
      </Root>
    );
  }

  return (
    <Root>
      <Background />
      <ContentCard>
        <Typography
          variant="h5"
          fontFamily="'Gyparody', 'Bangers', 'Impact', sans-serif"
          sx={{ mb: 2, color: ACCENT_TEAL }}
        >
          You're Ready!
        </Typography>
        <Box sx={{ fontSize: '3rem', mb: 2 }}>
          👍
        </Box>
        <Typography sx={{ fontSize: '1.2rem', fontWeight: 600, color: TEXT_COLOR, mb: 1 }}>
          Waiting for other players...
        </Typography>
        <Typography sx={{ fontSize: '1rem', color: TEXT_COLOR, mb: 2 }}>
          {nextRoundName} will start when everyone is ready, or the host starts it.
        </Typography>
        <Typography sx={{ fontSize: '0.9rem', color: '#666', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <CircularProgress size={16} thickness={6} sx={{ color: ACCENT_TEAL }} />
          {readyCount} of {totalCount} players ready
        </Typography>
      </ContentCard>
      <PlayerLeaderboard />
    </Root>
  );
};

export default PlayerInterRoundScreen;
